/**
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/
 */
import { __ } from '@wordpress/i18n';
import { useSelect, useDispatch  } from '@wordpress/data';
import { useState, useEffect} from '@wordpress/element';

/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { RichText, useBlockProps } from '@wordpress/block-editor';
/**
 * Internal dependencies
 */
import Controls from './controls.js';
import InsSettings from './settings.js';
import getUniqueId from '../../helpers/get-unique-id.js';

import { flattenIconsArray,parseIcon } from '../icon/utils';
import getIcons from '../icon/icons';
import { bolt as defaultIcon } from '../icon/icons/bolt';

import './editor.scss';

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {WPElement} Element to render.
 */

import { VayuBlocksDevice } from '@vayu-blocks/device-control';
import {VayuBlockAdvanceStyle} from '@vayu-blocks/advance-panel/style.js';
import { borderStyle, borderRadiusStyle,boxShadow,dimensions , background,VayuBlocksTypography} from "@vayu-blocks/global";

export default function Edit(props) {

	const isEmpty = (value) => {
		if (value == null) return true; // null or undefined
		if (typeof value === 'string' || Array.isArray(value)) return value.length === 0;
		if (typeof value === 'object') return Object.keys(value).length === 0;
		return false; // numbers, booleans, functions, etc.
	};

		const { attributes, setAttributes, clientId, uniqueID } = props;
		const { mergedStyles, handleMouseEnter, handleMouseLeave,hoverAnimationClass, responsiveClasses } = VayuBlockAdvanceStyle(attributes);

		 const [isHovered, setIsHovered] = useState(false);
		 const handleMouseEnterbutton = () => setIsHovered(true);
		 const handleMouseLeavebutton = () => setIsHovered(false);

		const getView = VayuBlocksDevice(); // Assume this is globally available 	// get device view

		const omitBy = (object, condition) => (
			Object.fromEntries(
			  Object.entries(object).filter(([key, value]) => !condition(value))
			)
		  );

		const buttonStyle = {
				padding: dimensions(attributes.btnPadding, getView),
				...borderStyle(attributes.btnBorder, getView),
				...borderRadiusStyle(attributes.btnBorderRadius, getView),
				...boxShadow(attributes.btnDropShadow, getView),
				...background(attributes.btnBackground),
				...VayuBlocksTypography(attributes.typography, getView),
				color: attributes.color,
			};
			const hoverStyles = {
				 ...borderStyle(attributes.btnBorderHover, getView, attributes.btnBorder),
				...borderRadiusStyle(attributes.btnBorderRadiusHover, getView, attributes.btnBorderRadius),
				...boxShadow(attributes.btnDropShadowHover, getView, attributes.btnDropShadow),
				...background(attributes.btnBackgroundHover),
				color: attributes.hoverColor,
			};
		
		const filteredHoverStyles = omitBy(hoverStyles, value => !value);

		const getBorderValue = (border, side, property) => {
			if (!border?.Desktop) return undefined;
		
			// Mixed case: Desktop.top, Desktop.right, etc. are objects
			if (typeof border.Desktop[side] === 'object' && border.Desktop[side]?.[property]) {
				return border.Desktop[side][property];
			}
		
			// Non-mixed case: Desktop itself contains the properties
			if (border.Desktop?.[property]) {
				return border.Desktop[property];
			}
		
			return undefined;
		};

		const getBorderRadiusValue = (radius, corner) => {
			if (!radius?.Desktop) return undefined;
		
			return radius.Desktop?.[corner] || undefined;
		};

		let variablestyle = {
			...(attributes.animationData && { '--animation-box-button': attributes.animationData?.background?.bg }),
			...(attributes.color && { '--button-color': attributes.color }),
			...(attributes.hoverColor && { '--button-hvr-color': attributes.hoverColor }),
			...(attributes.btnBackground && { '--button-background': attributes.btnBackground.color }),
			...(attributes.btnBackground && { '--button-gradient': attributes.btnBackground.gradient }),
			...(attributes.btnBackgroundHover && { '--button-hvr-background': attributes.btnBackgroundHover.color }),
			...(attributes.btnBackgroundHover && { '--button-hvr-gradient': attributes.btnBackgroundHover.gradient }),
			
			...(attributes.btnBorderRadius && { '--btnRadius-top-left': getBorderRadiusValue(attributes.btnBorderRadius, 'topLeft') }),
			...(attributes.btnBorderRadius && { '--btnRadius-top-right': getBorderRadiusValue(attributes.btnBorderRadius, 'topRight') }),
			...(attributes.btnBorderRadius && { '--btnRadius-bottom-left': getBorderRadiusValue(attributes.btnBorderRadius, 'bottomLeft') }),
			...(attributes.btnBorderRadius && { '--btnRadius-bottom-right': getBorderRadiusValue(attributes.btnBorderRadius, 'bottomRight') }),
		
			...(attributes.btnBorderRadiusHover && { '--btnRadiusHover-top-left': getBorderRadiusValue(attributes.btnBorderRadiusHover, 'topLeft') }),
			...(attributes.btnBorderRadiusHover && { '--btnRadiusHover-top-right': getBorderRadiusValue(attributes.btnBorderRadiusHover, 'topRight') }),
			...(attributes.btnBorderRadiusHover && { '--btnRadiusHover-bottom-left': getBorderRadiusValue(attributes.btnBorderRadiusHover, 'bottomLeft') }),
			...(attributes.btnBorderRadiusHover && { '--btnRadiusHover-bottom-right': getBorderRadiusValue(attributes.btnBorderRadiusHover, 'bottomRight') }),

		}

		let colorStyleButton; 
		let buttonFullWidth;
 
		colorStyleButton = {
		  '--color':attributes.color,
		  '--color-hvr':attributes.hoverColor,
		};

		if(attributes.alignment[getView] == 'justify'){
			buttonFullWidth={
				'width': '100%',
				'justifyContent': 'center',
			}
		}
		const mergedStylesbutton = {
			...buttonStyle,
			...(isHovered ? filteredHoverStyles : {}),
			...colorStyleButton,
			...buttonFullWidth,
			...variablestyle,
			
		};

	const { id } = attributes;

	const { addUniqueID } = useDispatch( 'vayu-blocks/data' );
	
	const { isUniqueID, isUniqueBlock} = useSelect(
				( select ) => {
					return {
						isUniqueID: ( value ) => select( 'vayu-blocks/data' ).isUniqueID( value ),
						isUniqueBlock: ( value, clientId ) => select( 'vayu-blocks/data' ).isUniqueBlock( value, clientId ),
						
					};
				},
				[ clientId ]
			);

			useEffect( () => {
				const uniqueId = getUniqueId( uniqueID, clientId, isUniqueID, isUniqueBlock );

					if ( uniqueId !== uniqueID ) {
						attributes.uniqueID = uniqueId;
						setAttributes( { uniqueID: uniqueId } );
						addUniqueID( uniqueId, clientId );
					} else {
						addUniqueID( uniqueId, clientId );
					}

				}, [] );


				  
	if ( ! id ) {
		setAttributes( { id: clientId } );
	}   

	let buttonWrapperStyle;
	let iconPositionStyle;
	


	if(attributes.iconPosition == 'before'){
		iconPositionStyle = {
			'--orderButton': 5,
			...(attributes.iconSpacing && { '--iconMargin': `0 ${ attributes.iconSpacing } 0 0` }),
			// '--iconMargin' : `0 ${desktopSpacing }px 0 0`
		  };
	}
	else{
		iconPositionStyle = {
			'--orderButton': 15,
			// '--iconMargin' : `0 0 0 ${ desktopSpacing }px`,
			...(attributes.iconSpacing && { '--iconMargin': `0 0 0 ${ attributes.iconSpacing }` }),
		  };
	}

	buttonWrapperStyle = {
		'textAlign': attributes.alignment[getView], 
	}
	
	const wrapperstyle = { 
		...iconPositionStyle,
		...buttonWrapperStyle,
		...mergedStyles,
		
	}

	const blockProps = useBlockProps({
			// id: attributes.id,
			className: `th-button-wrapper`,
			style: wrapperstyle,
			onMouseEnter: handleMouseEnter,
        	onMouseLeave: handleMouseLeave,
	});


	const { animationData = { background: { value: 'none', type: 'onhvr' } } } = attributes;

	const blockPropsButton = {
		className: `th-button th-button-inside  ${hoverAnimationClass} ${responsiveClasses} effect-trigger-${animationData.background.type} ${
            animationData.background.value !== 'none' ? animationData.background.value : ''
          }`,
		style: mergedStylesbutton,
		onMouseEnter: handleMouseEnterbutton,
		onMouseLeave: handleMouseLeavebutton,
        
};

// Vayu Icon Rendering
const { icon, iconName, iconToggle} = attributes;


const iconsAll = flattenIconsArray( getIcons() );
const namedIcon = iconsAll.filter( ( i ) => i.name === iconName );
let customIcon = defaultIcon;

if ( icon && isEmpty( namedIcon ) ) {
	customIcon = parseIcon( icon );

	if ( isEmpty( customIcon?.props ) ) {
		customIcon = defaultIcon;
	}
}

let printedIcon = ! isEmpty( namedIcon ) ? namedIcon[ 0 ].icon : customIcon;

// Icons provided by third-parties are generally strings.
if ( typeof printedIcon === 'string' ) {
	printedIcon = parseIcon( printedIcon );
}

function reactElementToSvgString(element) {
    if (element){
        let viewBox = `viewBox="0 0 24 24" `;
        if (element && element.props && element.props.viewBox) {
            viewBox = `viewBox="${element.props.viewBox}" `;
        }
        let svgString = `<svg xmlns="http://www.w3.org/2000/svg" ${viewBox}">`;
        if (element && element.props && element.props.children) {
            if (Array.isArray(element.props.children)) {
                element.props.children.forEach(child => {
                    if (child.props && child.props.d) {
                        svgString += `<path d="${child.props.d}" />`;
                    }
                });
        } else {
            const child = element.props.children;
                if (child.props && child.props.d) {
                    svgString += `<path d="${child.props.d}" />`;
                }
            }
        }
        svgString += `</svg>`;
        return svgString;
    }
}
useEffect(() => {
    if (React.isValidElement(printedIcon)) {
        const svgString = reactElementToSvgString(printedIcon);
        setAttributes({ iconCopy: svgString });
    }
}, [printedIcon, setAttributes]);
  
const iconSize = attributes?.typography?.fontSize?.[getView] || '';

// Markup for the icon
const iconMarkup = (
	<>
		{ ! icon && ! iconName ? (
			<IconPlaceholder
				setInserterOpen={ setInserterOpen }
				isQuickInserterOpen={ isQuickInserterOpen }
				setQuickInserterOpen={ setQuickInserterOpen }
				isCustomInserterOpen={ isCustomInserterOpen }
				setCustomInserterOpen={ setCustomInserterOpen }
				attributes={ attributes }
				setAttributes={ setAttributes }
				enableCustomIcons={ enableCustomIcons }
				isSVGUploadAllowed={ isSVGUploadAllowed }
			/>
		) : (
			<div
				className={`vayu_blocks_icon_block_main_icon_svg`}
				style={{ width: `${iconSize}`}}
				
			>
				{	printedIcon }

			</div>
		) }
	</>
);

		return (
			<>
			<Controls
				attributes={ attributes }
				setAttributes={ setAttributes }
			/>
			<InsSettings
    		props ={props}
    		/>
			<div  { ...blockProps }>
				<div { ...blockPropsButton }>
					{/* <span dangerouslySetInnerHTML={ { __html: sanitizeSVG( attributes.icon ) } } /> */}
					
					{iconToggle && <span className='vayu-icon'>{iconMarkup}</span>}

					<RichText
						tagName="span" // The tag here is the element output and editable in the admin
						value={ attributes.content } // Any existing content, either from the database or an attribute default
						allowedFormats={ [ 'core/bold', 'core/italic' ] } // Allow the content to be made bold or italic, but do not allow other formatting options
						onChange={ ( content ) => setAttributes( { content } ) } // Store updated content as a block attribute
						placeholder={ __( 'Button...','vayu-blocks' ) } // Display this text before any content has been added by the user
						className="vayu-btn-text"
					/>
				</div>
			</div>
			
			</>
        );
}
